/**
 * Base Schema Adapter
 *
 * This provides a middle layer between Frontend interfaces and Backend schemas.
 * It handles:
 * - Schema validation with fallback defaults
 * - Field mapping between FE/BE naming conventions
 * - Error handling for missing/undefined fields
 * - Type safety with strict TypeScript interfaces
 */

import { SchemaValidator } from "./SchemaValidator";

import Logger from "@/helper/logger";
import { toCamelCase } from "@/helper/toCamelCase";
import { toSnakeCase } from "@/helper/toSnakeCase";

export interface SchemaAdapterOptions {
  /** Enable logging for debugging schema mismatches */
  enableLogging?: boolean;
  /** Custom field mapping overrides */
  fieldMapping?: Record<string, string>;
  /** Custom max depth for validation */
  validatorMaxDepth?: number;
}

export interface AdapterResult<T> {
  data: T;
  warnings: string[];
  errors: string[];
  isValid: boolean;
  meta?: Record<string, unknown>;
}
const DEFAULT_MAX_DEPTH = 5;

export abstract class SchemaAdapter<TFrontend, TBackend> {
  protected options: Required<SchemaAdapterOptions>;
  protected adapterName: string;
  protected camelCaseSpecialConversion: Record<string, string> = {};
  protected snakeCaseSpecialConversion: Record<string, string> = {};
  protected validator: SchemaValidator;

  public constructor(adapterName: string, options: SchemaAdapterOptions = {}) {
    this.adapterName = adapterName;
    this.options = {
      enableLogging: true,
      fieldMapping: {},
      validatorMaxDepth: DEFAULT_MAX_DEPTH,
      ...options,
    };

    this.validator = new SchemaValidator(adapterName, this.options.validatorMaxDepth);
  }

  public abstract getDefaultClient(): TFrontend;
  public abstract getDefaultServer(): TBackend;

  protected flattenJsonConfig(config: Record<string, unknown>): Record<string, string> {
    const flattened: Record<string, string> = {};

    const flatten = (obj: Record<string, unknown>, prefix = ""): void => {
      for (const [key, value] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${key}` : key;

        if (typeof value === "string") {
          flattened[path] = value;
        } else if (typeof value === "object" && value !== null) {
          flatten(value as Record<string, unknown>, path);
        }
      }
    };

    flatten(config);
    return flattened;
  }

  /**
   * Convert backend schema to frontend interface with validation and defaults
   * @param backendData - The raw backend data to transform
   * @returns The adapter result with transformed data
   */
  public fromBackend(backendData: unknown): AdapterResult<TFrontend> {
    const validationResult = this.validateFromBackend(backendData);

    if (!validationResult.isValid) {
      this.log({
        data: this.getDefaultClient(),

        warnings: validationResult.warnings,
        errors: validationResult.errors,
        isValid: false,
      });

      return {
        data: this.getDefaultClient(),

        warnings: validationResult.warnings,
        errors: validationResult.errors,
        isValid: false,
      };
    }

    const backendApiKey = validationResult.data;

    const frontendData = toCamelCase<TFrontend>({
      object: backendApiKey,
      options: {
        specialConversion: this.camelCaseSpecialConversion,
      },
    });

    return {
      data: frontendData,
      warnings: validationResult.warnings,
      errors: validationResult.errors,
      isValid: validationResult.isValid,
    };
  }

  /**
   * Transform array of backend API keys to frontend format
   * @param backendArray - The backend array data
   * @returns The adapter result with frontend API key array
   */
  public fromBackendArray(backendArray: unknown): AdapterResult<TFrontend[]> {
    return this.transformArray(backendArray, (item) => this.fromBackend(item));
  }

  /**
   * Convert frontend interface to backend schema
   * @param frontendData - The frontend data to transform
   * @returns The backend schema data
   */
  public toBackend(frontendData: unknown): Partial<TBackend> {
    const backendApiKey = toSnakeCase<TBackend>({
      object: frontendData,
      options: {
        specialConversion: this.snakeCaseSpecialConversion,
      },
    });

    return backendApiKey;
  }

  /**
   * Validate required fields are present
   * @param data - The data object to validate
   * @param requiredFields - Array of required field names
   * @returns Array of validation error messages
   */
  protected validateRequired(data: unknown, requiredFields: string[]): string[] {
    const errors: string[] = [];

    if (!data || typeof data !== "object") {
      return [`${this.adapterName}: Invalid data structure - expected object`];
    }

    const dataObj = data as Record<string, unknown>;

    for (const field of requiredFields) {
      if (!(field in dataObj) || dataObj[field] === null || dataObj[field] === undefined) {
        errors.push(`${this.adapterName}: Missing required field '${field}'`);
      }
    }

    return errors;
  }

  /**
   * Safely extract field with fallback
   * @param data - The data object to extract from
   * @param field - The field name to extract
   * @param defaultValue - The default value if field is missing/invalid
   * @param validator - Optional validator function
   * @returns Object with extracted value and warnings
   */
  protected safeExtract<T>(
    data: Record<string, unknown>,
    field: string,
    defaultValue: T,
    validator?: (value: unknown) => value is T
  ): { value: T; warnings: string[] } {
    const warnings: string[] = [];

    // Check if field exists in data
    if (!(field in data) || data[field] === null || data[field] === undefined) {
      warnings.push(`${this.adapterName}: Field '${field}' is missing, using default value`);
      return { value: defaultValue, warnings };
    }

    const value = data[field];

    // If validator is provided, use it
    if (validator && !validator(value)) {
      warnings.push(`${this.adapterName}: Field '${field}' has invalid type, using default value`);
      return { value: defaultValue, warnings };
    }

    // Type assertion for basic types
    if (typeof defaultValue === "string" && typeof value !== "string") {
      warnings.push(`${this.adapterName}: Field '${field}' expected string, got ${typeof value}, using default`);
      return { value: defaultValue, warnings };
    }

    if (typeof defaultValue === "number" && typeof value !== "number") {
      warnings.push(`${this.adapterName}: Field '${field}' expected number, got ${typeof value}, using default`);
      return { value: defaultValue, warnings };
    }

    if (typeof defaultValue === "boolean" && typeof value !== "boolean") {
      warnings.push(`${this.adapterName}: Field '${field}' expected boolean, got ${typeof value}, using default`);
      return { value: defaultValue, warnings };
    }

    return { value: value as T, warnings };
  }

  /**
   * Log adapter results if logging is enabled
   * @param result - The adapter result to log
   * @returns void
   */
  protected log(result: AdapterResult<TFrontend>): void {
    if (!this.options.enableLogging) return;

    if (result.warnings.length > 0) {
      Logger.warn(`[${this.adapterName}] Schema Warnings:`, {
        context: this.adapterName,
        data: result.warnings,
      });
    }

    if (result.errors.length > 0) {
      Logger.error(`[${this.adapterName}] Schema Errors:`, {
        context: this.adapterName,
        data: result.errors,
      });
    }
  }

  protected validateFromBackend(backendData: unknown): AdapterResult<TBackend> {
    const validationResult = this.validator.validate<TBackend>(backendData, this.getDefaultServer());

    return validationResult;
  }

  /**
   * Handle array data transformation
   * @param data - The array data to transform
   * @param itemTransformer - Function to transform each item
   * @returns The adapter result with transformed array
   */
  protected transformArray<TItem>(
    data: unknown,
    itemTransformer: (item: unknown) => AdapterResult<TItem>
  ): AdapterResult<TItem[]> {
    const warnings: string[] = [];
    const errors: string[] = [];
    const transformedItems: TItem[] = [];

    if (!Array.isArray(data)) {
      errors.push(`${this.adapterName}: Expected array but got ${typeof data}`);
      return {
        data: [],
        warnings,
        errors,
        isValid: false,
      };
    }

    for (let i = 0; i < data.length; i++) {
      const itemResult = itemTransformer(data[i]);
      transformedItems.push(itemResult.data);
      warnings.push(...itemResult.warnings.map((warning) => `[Item ${i}] ${warning}`));
      errors.push(...itemResult.errors.map((error) => `[Item ${i}] ${error}`));
    }

    return {
      data: transformedItems,
      warnings,
      errors,
      isValid: errors.length === 0,
    };
  }
}
